import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Activity, ExternalLink } from 'lucide-react';
import { FUNCTION_HEALTH_AFFILIATE_LINK } from '../utils/constants';

interface FunctionHealthLinkProps {
  className?: string;
  text?: string;
  showIcon?: boolean;
  showDisclosure?: boolean;
}

const FunctionHealthLink: React.FC<FunctionHealthLinkProps> = ({
  className = '',
  text = 'Know your real numbers',
  showIcon = true,
  showDisclosure = true
}) => {
  const prefersReducedMotion = useReducedMotion();

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    window.open(FUNCTION_HEALTH_AFFILIATE_LINK, '_blank', 'noopener,noreferrer');
  };

  // Reduced motion version
  if (prefersReducedMotion) {
    return (
      <span className={`inline-flex items-center gap-1 ${className}`}>
        <a
          href={FUNCTION_HEALTH_AFFILIATE_LINK}
          onClick={handleClick}
          className="inline-flex items-center gap-1 text-teal-600 dark:text-catppuccin-teal hover:text-teal-700 dark:hover:text-catppuccin-green text-sm font-medium transition-colors duration-200 hover:underline"
        >
          {showIcon && <Activity size={14} className="flex-shrink-0" />}
          <span>{text}</span>
          <ExternalLink size={12} className="flex-shrink-0 opacity-70" />
        </a>
        {showDisclosure && (
          <span className="text-xs text-gray-400 dark:text-catppuccin-overlay1 ml-1">
            (partner)
          </span>
        )}
      </span>
    );
  }

  // Animated version
  return (
    <span className={`inline-flex items-center gap-1 ${className}`}>
      <motion.a
        href={FUNCTION_HEALTH_AFFILIATE_LINK}
        onClick={handleClick}
        className="inline-flex items-center gap-1 text-teal-600 dark:text-catppuccin-teal hover:text-teal-700 dark:hover:text-catppuccin-green text-sm font-medium transition-colors duration-200"
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
      >
        {showIcon && (
          <motion.span
            animate={{ scale: [1, 1.1, 1] }}
            transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
          >
            <Activity size={14} className="flex-shrink-0" />
          </motion.span>
        )}
        <span className="hover:underline">{text}</span>
        <ExternalLink size={12} className="flex-shrink-0 opacity-70" />
      </motion.a>
      {showDisclosure && (
        <motion.span
          className="text-xs text-gray-400 dark:text-catppuccin-overlay1 ml-1"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          (partner)
        </motion.span>
      )}
    </span>
  );
};

export default FunctionHealthLink;
